import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Highlighter, AArrowUp, AArrowDown } from "lucide-react";
import { sanitizeRich } from "@/lib/richtext";

const HIGHLIGHTS = [
  { name: "yellow", color: "#FEF08A" }, { name: "green", color: "#BBF7D0" },
  { name: "pink", color: "#FBCFE8" }, { name: "blue", color: "#BFDBFE" },
];
const TOOLS = [
  { cmd: "bold", icon: Bold, label: "Bold" }, { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "underline", icon: Underline, label: "Underline" }, { cmd: "strikeThrough", icon: Strikethrough, label: "Strikethrough" },
  { cmd: "insertUnorderedList", icon: List, label: "Bulleted list" }, { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
];

const RichEditor = forwardRef(function RichEditor({ value, onChange, placeholder = "", testid = "rich-editor", minHeight = 72, onSubmit }, ref) {
  const el = useRef(null);
  const [active, setActive] = useState({});
  const [hlOpen, setHlOpen] = useState(false);

  useImperativeHandle(ref, () => ({ clear: () => { if (el.current) el.current.innerHTML = ""; setActive({}); onChange(""); }, focus: () => el.current?.focus() }));

  useEffect(() => {
    if (el.current && el.current.innerHTML !== (value || "")) el.current.innerHTML = sanitizeRich(value || "");
  }, [value]);

  useEffect(() => {
    const onSel = () => {
      if (!el.current || !el.current.contains(document.getSelection()?.anchorNode)) return;
      const s = {};
      TOOLS.forEach((t) => { try { s[t.cmd] = document.queryCommandState(t.cmd); } catch {} });
      setActive(s);
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  const emit = () => onChange(el.current?.innerHTML || "");
  const exec = (cmd, arg) => { el.current?.focus(); document.execCommand("styleWithCSS", false, cmd === "hiliteColor"); document.execCommand(cmd, false, arg); emit(); };
  const size = (delta) => {
    el.current?.focus();
    const raw = parseInt(document.queryCommandValue("fontSize"), 10);
    const cur = !raw || raw < 3 ? 3 : raw;
    exec("fontSize", String(Math.max(1, Math.min(7, cur + delta))));
  };
  const btn = "h-7 w-7 inline-flex items-center justify-center rounded hover:bg-black/5 text-neutral-700";

  const onPaste = (e) => {
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    emit();
  };

  return (
    <div className="border border-black/20 bg-white rounded-md focus-within:border-black" data-testid={testid}>
      <div className="flex items-center gap-0.5 flex-wrap px-1.5 py-1 border-b border-black/10 relative" data-testid={`${testid}-toolbar`}>
        {TOOLS.map(({ cmd, icon: Icon, label }) => (
          <button key={cmd} type="button" title={label} aria-label={label} data-testid={`${testid}-${cmd}`}
            onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
            className={`${btn} ${active[cmd] ? "bg-black/10 text-black" : ""}`}><Icon size={14} /></button>
        ))}
        <span className="w-px h-4 bg-black/10 mx-1" />
        <div className="relative">
          <button type="button" title="Highlight" aria-label="Highlight" data-testid={`${testid}-highlight`} onMouseDown={(e) => { e.preventDefault(); setHlOpen(!hlOpen); }} className={btn}><Highlighter size={14} /></button>
          {hlOpen && (
            <div className="absolute z-20 top-8 left-0 bg-white border border-black/10 shadow-lg rounded-md p-1.5 flex gap-1" data-testid={`${testid}-highlight-menu`}>
              {HIGHLIGHTS.map((h) => (
                <button key={h.name} type="button" title={h.name} data-testid={`${testid}-hl-${h.name}`} onMouseDown={(e) => { e.preventDefault(); exec("hiliteColor", h.color); setHlOpen(false); }}
                  className="h-5 w-5 rounded-full border border-black/10" style={{ background: h.color }} />
              ))}
              <button type="button" title="No highlight" data-testid={`${testid}-hl-none`} onMouseDown={(e) => { e.preventDefault(); exec("hiliteColor", "transparent"); setHlOpen(false); }}
                className="h-5 w-5 rounded-full border border-black/20 text-[10px] leading-none">✕</button>
            </div>
          )}
        </div>
        <button type="button" title="Increase font size" aria-label="Increase font size" data-testid={`${testid}-size-up`} onMouseDown={(e) => { e.preventDefault(); size(1); }} className={btn}><AArrowUp size={15} /></button>
        <button type="button" title="Decrease font size" aria-label="Decrease font size" data-testid={`${testid}-size-down`} onMouseDown={(e) => { e.preventDefault(); size(-1); }} className={btn}><AArrowDown size={15} /></button>
      </div>
      <div ref={el} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true"
        data-testid={`${testid}-input`} data-placeholder={placeholder}
        onInput={emit} onPaste={onPaste}
        onKeyDown={(e) => { if (onSubmit && e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onSubmit(); } }}
        className="rich-content rich-editor px-3 py-2 text-sm outline-none overflow-auto max-h-60" style={{ minHeight }} />
    </div>
  );
});

export default RichEditor;
